<template>
  <div class="welcome-card ai-organizer-card">
    <div class="card-header">
      <div class="card-title-group">
        <i class="fas fa-clipboard-list card-title-icon"></i>
        <h3 class="card-title">AI Organizer</h3>
      </div>
      <div class="tab-pills">
        <button
          v-for="tab in tabs"
          :key="tab"
          class="tab-pill"
          :class="{ 'tab-pill--active': activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
        </button>
      </div>
    </div>

    <div class="card-body">
      <template v-if="isLoading">
        <div class="todo-skeleton" v-for="n in 3" :key="n"></div>
      </template>
      <template v-else-if="filteredTodos.length === 0">
        <span class="todo-empty"
          >No {{ activeTab.toLowerCase() }} items yet</span
        >
      </template>
      <template v-else>
        <label v-for="todo in filteredTodos" :key="todo.id" class="todo-row">
          <input
            type="checkbox"
            class="todo-checkbox"
            :checked="todo.done"
            @change="updateTodo({ id: todo.id, done: !todo.done })"
          />
          <span v-if="todo.type === 'list'" class="todo-bullet">•</span>
          <span class="todo-label" :class="{ 'todo-label--done': todo.done }">
            {{ todo.text }}
          </span>
          <button
            class="todo-delete"
            @click.prevent="deleteTodo(todo.id)"
            title="Delete"
          >
            ×
          </button>
        </label>
      </template>
    </div>

    <div class="card-footer">
      <input
        v-model="newTodoText"
        type="text"
        class="add-todo-input"
        :placeholder="addPlaceholder"
        @keydown.enter.prevent="handleAdd"
      />
      <button
        class="add-todo-button"
        :disabled="!newTodoText.trim()"
        @click="handleAdd"
        title="Add"
      >
        <i class="fas fa-plus"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "AIOrganizerCard",
  data() {
    return {
      tabs: ["To-Do", "List", "Agenda"],
      activeTab: "To-Do",
      newTodoText: "",
    };
  },
  computed: {
    ...mapState("oliverTodos", ["todos", "isLoading"]),
    filteredTodos() {
      const typeMap = { "To-Do": "todo", List: "list", Agenda: "agenda" };
      return this.todos.filter((t) => t.type === typeMap[this.activeTab]);
    },
    addPlaceholder() {
      const map = {
        "To-Do": "Add to-do item...",
        List: "Add list item...",
        Agenda: "Add agenda item...",
      };
      return map[this.activeTab];
    },
  },
  mounted() {
    this.fetchTodos();
  },
  methods: {
    ...mapActions("oliverTodos", [
      "fetchTodos",
      "createTodo",
      "updateTodo",
      "deleteTodo",
    ]),
    async handleAdd() {
      const text = this.newTodoText.trim();
      if (!text) return;
      this.newTodoText = "";
      const typeMap = { "To-Do": "todo", List: "list", Agenda: "agenda" };
      await this.createTodo({ text, type: typeMap[this.activeTab] });
    },
  },
};
</script>

<style lang="scss" scoped>
.welcome-card {
  background: var(--oliver-bg-surface, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  border-radius: 14px;
  padding: 8px 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(6px);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  background: none;
  border: none;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title-icon {
  font-size: 14px;
  color: var(--oliver-accent);
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oliver-text);
  margin: 0;
}

.tab-pills {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  padding: 2px;
  gap: 0;
}

.tab-pill {
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: var(--oliver-text);
  opacity: 0.6;
  padding: 5px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &--active {
    background: var(--oliver-accent);
    color: #fff;
    opacity: 1;
    font-weight: 600;
  }

  &:hover:not(&--active) {
    opacity: 0.9;
  }
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
}

.todo-skeleton {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.03) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  margin: 3px 0;

  &:nth-child(2) {
    width: 75%;
  }
  &:nth-child(3) {
    width: 55%;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.todo-empty {
  font-size: 12px;
  color: var(--oliver-greeting-subtitle);
  text-align: center;
  margin: auto;
}

.todo-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1px 0;
  cursor: pointer;
  user-select: none;
  margin: 0;

  &:hover .todo-delete {
    opacity: 0.4;
  }
}

.todo-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  background: white;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:checked {
    background: var(--oliver-accent);
    border-color: var(--oliver-accent);

    &::after {
      content: "";
      position: absolute;
      left: 4px;
      top: 1px;
      width: 5px;
      height: 9px;
      border: solid var(--oliver-text);
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

.todo-bullet {
  font-size: 14px;
  color: var(--oliver-text);
  opacity: 0.7;
  flex-shrink: 0;
  width: 16px;
  text-align: center;
  line-height: 1;
}

.todo-delete {
  margin-left: auto;
  background: none;
  border: none;
  color: var(--oliver-text);
  opacity: 0;
  cursor: pointer;
  font-size: 16px;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1 !important;
  }
}

.todo-label {
  font-size: 13px;
  color: var(--oliver-text);
  line-height: 1.2;
  transition: opacity 0.2s ease, text-decoration 0.2s ease;

  &--done {
    text-decoration: line-through;
    opacity: 0.5;
  }
}

.card-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  padding: 5px 0 0;
  background: none;
}

.add-todo-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--oliver-text);
  outline: none;
  padding: 4px 0;

  &::placeholder {
    color: var(--oliver-text);
    opacity: 0.4;
  }
}

.add-todo-button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  border: none;
  color: var(--oliver-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--oliver-accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
